import { Grid, FormControlLabel, Checkbox, Typography } from '@material-ui/core';
import { Add, Remove } from '@material-ui/icons';
import { booleanOptions, BooleanValue, unidadeFederativaOptions, tipoInvestidorOptions } from 'common/genericOptions';
import { TipoInvestidor, BancoResponse } from 'common/genericTypes';
import { CepMask, CpfCnpjMask, TelefoneMask } from 'components/masks';
import { Button, Dropdown, Section, SmSpace, TextField, DatePicker, NumberField, AutoComplete } from 'm2m.components';
import React, { useState, useCallback, useEffect } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import {
  updateInformacoesPrincipaisField,
  updateEnderecoField,
  updateInformacoesParaContatoField,
  updateInformacoesBancariasField,
} from 'store/modules/investidores/registro';
import { labelsPf, labelsPj } from 'pages/investidores/registro/informacoesPrincipais/labels';
import { useValidarCampos, EtapaCadastro } from 'pages/investidores/registro/useValidarCampos';

const mapStateToProps = (state: RootState) => ({
  informacoesPrincipais: state.investidor.informacoesPrincipais,
  endereco: state.investidor.endereco,
  informacoesParaContato: state.investidor.informacoesParaContato,
  informacoesBancarias: state.investidor.informacoesBancarias,
  highlightErrors: state.investidor.highlightErrors,
  getBancosApi: state.catalogoApi.getBancos,
});

const mapDispatchToProps = {
  updateInformacoesPrincipaisField,
  updateEnderecoField,
  updateInformacoesParaContatoField,
  updateInformacoesBancariasField,
};

type Props = typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>;

const InformacoesPrincipais: React.FC<Props> = (props) => {
  const {
    informacoesPrincipais,
    endereco,
    informacoesParaContato,
    informacoesBancarias,
    highlightErrors,
    endereco: { cep },
    informacoesParaContato: { email },
    updateInformacoesPrincipaisField,
    updateEnderecoField,
    updateInformacoesParaContatoField,
    updateInformacoesBancariasField,
    getBancosApi,
  } = props;

  const [bancosOptions, setBancosOptions] = useState<BancoResponse[]>([]);
  const [selectedBanco, setSelectedBanco] = useState<BancoResponse | null>(null);

  const dispatch = useDispatch();

  const getBancos = useCallback(async () => {
    try {
      const result = await getBancosApi(dispatch, true);
      if (result && result.banks) {
        setBancosOptions(result.banks);
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  }, [dispatch, getBancosApi]);

  useEffect(() => {
    getBancos();
  }, [getBancos]);

  useEffect(() => {
    if (bancosOptions.length > 0 && informacoesBancarias.banco) {
      const banco = bancosOptions.find((b) => b.shortname === informacoesBancarias.banco);
      setSelectedBanco(banco || null);
    }
  }, [bancosOptions, informacoesBancarias.banco]);

  const bancosFilter = (value: string) =>
    bancosOptions.filter(
      (b) =>
        b.code.includes(value) ||
        b.fullname.toLocaleLowerCase().includes(value) ||
        b.ispb.includes(value) ||
        b.shortname.toLocaleLowerCase().includes(value)
    );

  const labels = informacoesPrincipais.isPj ? labelsPj : labelsPf;
  const camposInvalidos = useValidarCampos(labels, EtapaCadastro.InformacoesPrincipais);

  const [telefone, setTelefone] = useState<string>('');

  const getEnderecoByCepApi = useSelector((state: RootState) => state.catalogoApi.getEndereco);

  const autocompleteEndereco = useCallback(
    async (cep: string) => {
      try {
        if (cep?.length !== 9) return;

        const result = await getEnderecoByCepApi(dispatch, cep);
        if (result) {
          updateEnderecoField({ name: 'logradouro', value: result.logradouro });
          updateEnderecoField({ name: 'bairro', value: result.bairro });
          updateEnderecoField({ name: 'cidade', value: result.localidade });
          updateEnderecoField({ name: 'uf', value: result.uf });
        }
      } catch {}
    },
    [dispatch, getEnderecoByCepApi, updateEnderecoField]
  );

  const handleAddTelefone = () => {
    var telMatch = telefone.match(/\d+/g);
    if (!telefone || !telMatch || telMatch.join('').length < 10) return;

    const newTelefones = Array.from(new Set([...informacoesParaContato.telefones, telefone]));
    updateInformacoesParaContatoField({ name: 'telefones', value: newTelefones });
    setTelefone('');
  };

  const handleRemoveTelefone = (telefone: string) => {
    const newTelefones = informacoesParaContato.telefones.filter((tel) => tel !== telefone);
    updateInformacoesParaContatoField({ name: 'telefones', value: newTelefones });
  };

  return (
    <div>
      <Section title="Informações Principais" defaultGrid>
        <TextField
          disabled
          label={labels.cpfCnpj}
          value={CpfCnpjMask(informacoesPrincipais.cpfCnpj)}
          onChange={() => null}
          error={highlightErrors && camposInvalidos.includes(labels.cpfCnpj)}
        />
        <TextField
          label={labels.razaosocial}
          value={informacoesPrincipais.razaoSocial}
          onChange={(value) => updateInformacoesPrincipaisField({ name: 'razaoSocial', value })}
          required
          error={highlightErrors && camposInvalidos.includes(labels.razaosocial)}
        />
        <DatePicker
          label={labels.dataNascimento}
          value={informacoesPrincipais.dataNascimento}
          onChange={(value) => updateInformacoesPrincipaisField({ name: 'dataNascimento', value })}
          required
          error={highlightErrors && camposInvalidos.includes(labels.dataNascimento)}
        />
        <TextField
          label={labels.nacionalidade}
          value={informacoesPrincipais.nacionalidade}
          onChange={(value) => updateInformacoesPrincipaisField({ name: 'nacionalidade', value })}
          required
          error={highlightErrors && camposInvalidos.includes(labels.nacionalidade)}
        />
        <TextField
          label={labels.profissao}
          value={informacoesPrincipais.profissao}
          onChange={(value) => updateInformacoesPrincipaisField({ name: 'profissao', value })}
          required
          error={highlightErrors && camposInvalidos.includes(labels.profissao)}
        />
        <Dropdown
          label={labels.investidorPoliticamenteExposto}
          required
          options={booleanOptions}
          value={informacoesPrincipais.investidorPoliticamenteExposto ? BooleanValue.Yes : BooleanValue.No}
          onChange={(value) =>
            updateInformacoesPrincipaisField({
              name: 'investidorPoliticamenteExposto',
              value: value === BooleanValue.Yes,
            })
          }
          error={highlightErrors && camposInvalidos.includes(labels.investidorPoliticamenteExposto)}
        />
        <Dropdown
          label={labels.tipoInvestidor}
          required
          options={tipoInvestidorOptions}
          value={informacoesPrincipais.tipo}
          onChange={(value) =>
            updateInformacoesPrincipaisField({
              name: 'tipo',
              value: value as TipoInvestidor,
            })
          }
          error={highlightErrors && camposInvalidos.includes(labels.tipoInvestidor)}
        />
        <NumberField
          label={labels.rendaMensalDeclarada}
          value={informacoesPrincipais.rendaMensalDeclarada}
          onChange={(value) => updateInformacoesPrincipaisField({ name: 'rendaMensalDeclarada', value: value! })}
          numberType="money"
        />
        <NumberField
          label={labels.patrimonioDeclarado}
          value={informacoesPrincipais.patrimonioDeclarado}
          onChange={(value) => updateInformacoesPrincipaisField({ name: 'patrimonioDeclarado', value: value! })}
          numberType="money"
        />
        <FormControlLabel
          control={
            <Checkbox
              color={'primary'}
              checked={informacoesPrincipais.vinculadoCustodiante}
              onChange={() =>
                updateInformacoesPrincipaisField({
                  name: 'vinculadoCustodiante',
                  value: !informacoesPrincipais.vinculadoCustodiante,
                })
              }
            />
          }
          label={labels.vinculadoCustodiante}
        />
      </Section>
      <Section title={labels.endereco} defaultGrid>
        <TextField
          label={`${labels.cep} *`}
          value={CepMask(cep)}
          onChange={(value) => {
            updateEnderecoField({ name: 'cep', value });
          }}
          onBlur={(event) => autocompleteEndereco(event.target.value)}
          error={highlightErrors && camposInvalidos.includes(labels.cep)}
          errorMessage={'CEP Inválido'}
        />
        <TextField
          label={labels.logradouro}
          required
          value={endereco.logradouro}
          onChange={(value) => updateEnderecoField({ name: 'logradouro', value })}
          error={highlightErrors && camposInvalidos.includes(labels.logradouro)}
        />
        <TextField
          label={labels.numero}
          required
          value={endereco.numero}
          onChange={(value) => updateEnderecoField({ name: 'numero', value })}
          error={highlightErrors && camposInvalidos.includes(labels.numero)}
        />
        <TextField
          label={labels.complemento}
          value={endereco.complemento}
          onChange={(value) => updateEnderecoField({ name: 'complemento', value })}
        />
        <TextField
          label={labels.bairro}
          required
          value={endereco.bairro}
          onChange={(value) => updateEnderecoField({ name: 'bairro', value })}
          error={highlightErrors && camposInvalidos.includes(labels.bairro)}
        />
        <TextField
          label={labels.cidade}
          required
          value={endereco.cidade}
          onChange={(value) => updateEnderecoField({ name: 'cidade', value })}
          error={highlightErrors && camposInvalidos.includes(labels.cidade)}
        />
        <Dropdown
          label={labels.uf}
          required
          options={unidadeFederativaOptions}
          value={endereco.uf}
          onChange={(value) => updateEnderecoField({ name: 'uf', value })}
          error={highlightErrors && camposInvalidos.includes(labels.uf)}
        />
      </Section>
      <Section title={'Informações para Contato'} defaultGrid>
        <Grid container spacing={2} style={{ width: '100%' }}>
          <Grid item xs={12} md={12}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs>
                <TextField
                  label={labels.telefone}
                  required={!informacoesPrincipais.isPj}
                  value={TelefoneMask(telefone)}
                  onChange={(value) => setTelefone(value)}
                  error={highlightErrors && camposInvalidos.includes(labels.telefone)}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  style={{ minWidth: '0', padding: '5px', width: '45px', height: '45px' }}
                  onClick={handleAddTelefone}
                >
                  <Add />
                </Button>
              </Grid>
            </Grid>
            {informacoesParaContato.telefones.map((telefone, index) => (
              <Grid container spacing={2} alignItems="center" key={index}>
                <Grid item xs>
                  <SmSpace />
                  <TextField disabled label={'Telefone'} value={TelefoneMask(telefone)} onChange={() => null} />
                </Grid>
                <Grid item>
                  <SmSpace />
                  <Button
                    variant="contained"
                    color="error"
                    style={{ minWidth: '0', padding: '5px', width: '45px', height: '45px' }}
                    onClick={() => handleRemoveTelefone(telefone)}
                  >
                    <Remove />
                  </Button>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <TextField
          label={labels.email}
          value={email}
          onChange={(value) => updateInformacoesParaContatoField({ name: 'email', value })}
          error={highlightErrors && camposInvalidos.includes(labels.email)}
          errorMessage={'E-mail inválido'}
        />
      </Section>
      <Section title={'Informações Bancárias (Opcional)'} defaultGrid={false}>
        <Typography variant="caption" style={{ display: 'block', marginBottom: '16px', color: '#666' }}>
          * Preencha os dígitos da agência e conta nos campos específicos
        </Typography>
        <Grid container direction="column" spacing={3} style={{ maxWidth: '480px' }}>
          <Grid item>
            <AutoComplete
              label={labels.banco}
              options={bancosOptions || []}
              getOptionLabel={(option: BancoResponse) => `${option.code} - ${option.shortname}`}
              value={selectedBanco}
              onChange={(newValue) => {
                setSelectedBanco(newValue);
                if (newValue) {
                  updateInformacoesBancariasField({ name: 'banco', value: newValue.shortname });
                  updateInformacoesBancariasField({ name: 'ispbBanco', value: newValue.ispb });
                } else {
                  updateInformacoesBancariasField({ name: 'banco', value: '' });
                  updateInformacoesBancariasField({ name: 'ispbBanco', value: '' });
                }
              }}
              filterOptions={
                bancosOptions
                  ? (_options: Array<BancoResponse>, state) => bancosFilter(state.inputValue.toLocaleLowerCase())
                  : undefined
              }
            />
          </Grid>

          <Grid item>
            <Grid container spacing={0} alignItems="center">
              <Grid item xs={7}>
                <TextField
                  label={labels.agencia}
                  value={informacoesBancarias.agencia}
                  onChange={(rawValue: string) => {
                    const numericValue = String(rawValue).replace(/[^0-9]/g, '');
                    updateInformacoesBancariasField({ name: 'agencia', value: numericValue });
                  }}
                  fullWidth
                  type="text"
                  inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
                />
              </Grid>
              <Grid item xs={1} style={{ textAlign: 'center' }}>
                <Typography variant="h6" style={{ marginTop: '8px' }}>
                  -
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label={labels.agenciaDigito}
                  value={informacoesBancarias.agenciaDigito}
                  onChange={(rawValue: string) => {
                    const numericValue = String(rawValue).replace(/[^0-9]/g, '');
                    updateInformacoesBancariasField({ name: 'agenciaDigito', value: numericValue });
                  }}
                  fullWidth
                  type="text"
                  inputProps={{ maxLength: 2, inputMode: 'numeric', pattern: '[0-9]*' }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item>
            <Grid container spacing={0} alignItems="center">
              <Grid item xs={7}>
                <TextField
                  label={labels.conta}
                  value={informacoesBancarias.conta}
                  onChange={(rawValue: string) => {
                    const numericValue = String(rawValue).replace(/[^0-9]/g, '');
                    updateInformacoesBancariasField({ name: 'conta', value: numericValue });
                  }}
                  fullWidth
                  type="text"
                  inputProps={{ maxLength: 12, inputMode: 'numeric', pattern: '[0-9]*' }}
                />
              </Grid>
              <Grid item xs={1} style={{ textAlign: 'center' }}>
                <Typography variant="h6" style={{ marginTop: '8px' }}>
                  -
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label={labels.contaDigito}
                  value={informacoesBancarias.contaDigito}
                  onChange={(rawValue: string) => {
                    const numericValue = String(rawValue).replace(/[^0-9]/g, '');
                    updateInformacoesBancariasField({ name: 'contaDigito', value: numericValue });
                  }}
                  fullWidth
                  type="text"
                  inputProps={{ maxLength: 2, inputMode: 'numeric', pattern: '[0-9]*' }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Section>
    </div>
  );
};
export default connect(mapStateToProps, mapDispatchToProps)(InformacoesPrincipais);
